import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  styled,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import {
  type EpocusEncounter,
  type EpocusEncounterStatus,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusEncounterPatient,
  RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME,
} from "../../../../global-providers/rxdb-provider/collections/epocus-encounter-patient";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  useRxCollectionEpocusExam,
} from "../../../../global-providers/rxdb-provider/collections/epocus-exam";
import { useUser } from "../../../../global-providers/user-provider/provider";
import { formatDicomPatientName } from "../../_lib/dicom-patient-name";
import {
  DEVICE_LABELS,
  EXAM_ICONS,
  EXAM_LABELS,
} from "../../_lib/epocus-exam-constants";
import { UltrasoundScanner } from "../../_lib/epocus-icons";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { useImagingInstancesByAccessionNumber } from "../../_lib/hooks/use-encounter-imaging-instances";
import { usePerformerName } from "../../_lib/hooks/use-performer-name";
import { RoleGate } from "../../_lib/rbac/role-gate";
import {
  linkToEpocusAddExam,
  linkToEpocusExam,
  linkToEpocusHome,
} from "../../links";
import { AwaitingImagesSection } from "./_lib/awaiting-images-section";
import { submitEncounterForReview } from "./_lib/encounter-submission";
import { EncounterTimeline } from "./_lib/encounter-timeline";
import { LinkPatientSection } from "./_lib/link-patient-section";
import { PatientConsentGate } from "./_lib/patient-consent-gate";
import { PatientDetailsForm } from "./_lib/patient-details-form";
import { ReferPatientSheet } from "./_lib/refer-patient-sheet";
import { resolveScannedByName } from "./_lib/scan-attribution";
import { UnmatchedDicomSection } from "./_lib/unmatched-dicom-section";
import { useEncounterDicomImages } from "./_lib/use-encounter-dicom-images";

const STATUS_COLORS: Record<
  EpocusEncounterStatus,
  { bg: string; color: string }
> = {
  imported: { bg: "#e0f2fe", color: "#0284c7" },
  draft: { bg: "#fef3c7", color: "#d97706" },
  pending_review: { bg: "#dbeafe", color: "#2563eb" },
  reviewed: { bg: "#dcfce7", color: "#16a34a" },
  complete: { bg: "#f3f4f6", color: "#6b7280" },
};

const STATUS_LABELS: Record<EpocusEncounterStatus, string> = {
  imported: "Imported",
  draft: "Draft",
  pending_review: "Pending Review",
  reviewed: "Reviewed",
  complete: "Complete",
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 680,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const MetaGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 16,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
});

const MetaItem = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const MetaLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

const MetaValue = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
});

const ExamCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 10,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  minHeight: 64,
  "&:active": { opacity: 0.7 },
  "&:hover": { backgroundColor: "#f9fafb" },
});

const ExamIconBox = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: EPOCUS_SELECTED_BG,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  marginRight: 12,
});

const StyledTabs = styled(Tabs)({
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  "& .MuiTabs-indicator": {
    backgroundColor: EPOCUS_ICON_ACTIVE,
    height: 3,
  },
});

const StyledTab = styled(Tab)({
  textTransform: "none",
  fontWeight: 600,
  fontSize: rem(15),
  minHeight: 48,
  color: EPOCUS_TEXT_SECONDARY,
  "&.Mui-selected": {
    color: EPOCUS_ICON_ACTIVE,
  },
});

const PatientBillingSection: FC<{
  encounterId: string;
  savedPatient: EpocusEncounterPatient | null;
}> = ({ encounterId, savedPatient }) => {
  const [expanded, setExpanded] = useState(false);
  const hasAutoExpanded = useRef(false);

  // Auto-expand once when saved data arrives asynchronously
  useEffect(() => {
    if (savedPatient !== null && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      setExpanded(true);
    }
  }, [savedPatient]);

  return (
    <Box>
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        aria-expanded={expanded}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          backgroundColor: EPOCUS_WHITE,
          border: `1px solid ${EPOCUS_BORDER}`,
          borderRadius: expanded ? "12px 12px 0 0" : 12,
          cursor: "pointer",
        }}
      >
        <Typography
          style={{
            fontSize: rem(12),
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: EPOCUS_TEXT_SECONDARY,
          }}
        >
          Patient billing details
        </Typography>
        <Typography
          style={{
            fontSize: rem(16),
            color: EPOCUS_TEXT_SECONDARY,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
          aria-hidden
        >
          ▾
        </Typography>
      </Box>
      <Box
        aria-hidden={!expanded}
        style={{
          display: expanded ? "block" : "none",
          borderLeft: `1px solid ${EPOCUS_BORDER}`,
          borderRight: `1px solid ${EPOCUS_BORDER}`,
          borderBottom: `1px solid ${EPOCUS_BORDER}`,
          borderRadius: "0 0 12px 12px",
        }}
      >
        <PatientDetailsForm
          encounterId={encounterId}
          savedPatient={savedPatient}
          hideHeader
        />
      </Box>
    </Box>
  );
};

export const EpocusEncounterPage: FC = () => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const navigate = useNavigate();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examCollection = useRxCollectionEpocusExam();
  const user = useUser();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // PNE-212: exam removal — tracks which exam is pending delete confirmation
  const [removingExamId, setRemovingExamId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [referSheetOpen, setReferSheetOpen] = useState(false);
  const [referralsEnabled] = useFeatureFlag(featureFlags.epocus.referrals);
  const [billingCodesEnabled] = useFeatureFlag(
    featureFlags.epocus.billingCodes,
  );
  const visibleTab = referralsEnabled ? activeTab : 0;

  const handleRemoveExam = async (examId: string) => {
    if (!examCollection) return;
    try {
      const doc = await examCollection.findOne(examId).exec();
      await doc?.remove();
    } catch (err) {
      console.error("Failed to remove exam", { examId });
    } finally {
      setRemovingExamId(null);
    }
  };

  const { result: encounters, isFetching: encounterLoading } =
    useRxData<EpocusEncounter>(
      RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
      (collection) => collection.findOne(encounterId ?? ""),
    );
  const encounter = encounters?.[0] ?? null;

  // Canonical accession: the schema permits blank/whitespace values, so trim
  // once and map empty → null. Every consumer (the DICOM/instance queries and
  // the awaiting-images UI) uses this same normalized value so a stored
  // whitespace accession can't make the queries miss real ImagingInstances.
  const accessionNumber = encounter?.accessionNumber?.trim() || null;

  const { displayName: performerName, loading: performerLoading } =
    usePerformerName(encounter?.performerId);

  const { images: dicomImages, instanceCount: dicomInstanceCount } =
    useEncounterDicomImages({
      accessionNumber,
      dicomStudyId: encounter?.dicomStudyId ?? null,
      encounterId,
      examCollection,
      encounterCollection,
      currentDeviceId: encounter?.deviceId ?? null,
      userId: user.id,
    });
  const { instances: imagingInstances } =
    useImagingInstancesByAccessionNumber(accessionNumber);
  const { result: exams, isFetching: examsLoading } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      collection.find({ selector: { encounterId: encounterId ?? "" } }),
  );

  const { result: encounterPatients, isFetching: encounterPatientLoading } =
    useRxData<EpocusEncounterPatient>(
      RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME,
      (collection) => collection.findOne(encounterId ?? ""),
    );
  const encounterPatient = encounterPatients?.[0] ?? null;

  const isLoading = encounterLoading || examsLoading || encounterPatientLoading;

  // Images already present in any exam should not appear in the unmatched section.
  const assignedImageIds = useMemo(
    () => new Set(exams.flatMap((e) => e.images.map((img) => img.id))),
    [exams],
  );
  const pendingDicomImages = useMemo(
    () => dicomImages.filter((img) => !assignedImageIds.has(img.id)),
    [dicomImages, assignedImageIds],
  );

  // PNE-491: images only arrive via DICOM replication from the ultrasound
  // device — there is no manual upload path. Until any land, surface an
  // explicit "awaiting images" state naming the accession to enter on the
  // machine, provided the encounter has an accession to link the scan back to.
  const hasAnyImages =
    imagingInstances.length > 0 ||
    dicomInstanceCount > 0 ||
    exams.some((e) => e.images.length > 0);
  const awaitingImages = !hasAnyImages && accessionNumber !== null;

  const allExamsSubmitted =
    exams.length > 0 && exams.every((e) => e.status === "submitted");

  const canSubmitForReview =
    (encounter?.status === "draft" || encounter?.status === "imported") &&
    allExamsSubmitted;

  const handleSubmitForReview = async () => {
    if (!encounterCollection || !encounter) return;
    setSubmitError(null);
    try {
      await submitEncounterForReview(
        encounterCollection,
        encounter.id,
        user.id,
      );
    } catch (err) {
      setSubmitError("Failed to submit for review. Please try again.");
      console.error("Submit for review failed:", err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!encounter) {
    return (
      <PageRoot>
        <Typography>Encounter not found.</Typography>
      </PageRoot>
    );
  }

  const scannedByName = resolveScannedByName(
    performerLoading ? null : performerName,
    encounter.operatorName,
  );

  const status = encounter.status as EpocusEncounterStatus;
  const statusColors = STATUS_COLORS[status];
  const deviceLabel = encounter.deviceId
    ? (DEVICE_LABELS[encounter.deviceId] ?? encounter.deviceId)
    : "Unknown Device";

  return (
    <PageRoot>
      {encounterId && (
        <PatientConsentGate
          encounterId={encounterId}
          onCancel={() => navigate(linkToEpocusHome())}
        />
      )}

      <Button
        onClick={() => navigate(linkToEpocusHome())}
        style={{
          color: EPOCUS_PRIMARY,
          textTransform: "none",
          fontSize: rem(14),
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ‹ Back to encounters
      </Button>

      <Box>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          style={{ gap: 8 }}
        >
          <Typography
            variant="h4"
            style={{
              fontWeight: 800,
              color: EPOCUS_TEXT_PRIMARY,
              fontSize: rem(26),
            }}
          >
            {formatDicomPatientName(encounter.patientName) || "Unknown Patient"}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 8, flexShrink: 0 }}
          >
            {referralsEnabled && (
              <RoleGate can="referPatient">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setReferSheetOpen(true)}
                  style={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: rem(13),
                    borderRadius: 8,
                    minHeight: 36,
                    padding: "6px 12px",
                    borderColor: EPOCUS_ICON_ACTIVE,
                    color: EPOCUS_ICON_ACTIVE,
                  }}
                >
                  Refer Patient
                </Button>
              </RoleGate>
            )}
            <Chip
              label={STATUS_LABELS[status]}
              style={{
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                fontWeight: 700,
                fontSize: rem(13),
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
        <Typography
          variant="body2"
          style={{
            color: EPOCUS_TEXT_SECONDARY,
            marginTop: 4,
            fontSize: rem(13),
          }}
        >
          {deviceLabel} ·{" "}
          {performerLoading ? (
            <Skeleton
              variant="text"
              width={120}
              style={{ display: "inline-block" }}
            />
          ) : (
            performerName
          )}
        </Typography>
      </Box>

      <MetaGrid>
        <MetaItem>
          <MetaLabel>Date</MetaLabel>
          <MetaValue>{formatDate(encounter.date)}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Device</MetaLabel>
          <MetaValue>{deviceLabel}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Performer</MetaLabel>
          <MetaValue>
            {performerLoading ? (
              <Skeleton variant="text" width={140} />
            ) : (
              <>
                {performerName}
                {scannedByName && (
                  <Box
                    component="span"
                    display="block"
                    style={{
                      fontWeight: 400,
                      fontSize: rem(12),
                      color: EPOCUS_TEXT_SECONDARY,
                    }}
                  >
                    Scanned by {scannedByName}
                  </Box>
                )}
              </>
            )}
          </MetaValue>
        </MetaItem>
      </MetaGrid>

      {/* Tabs: Details | Timeline */}
      <Box>
        <StyledTabs
          value={visibleTab}
          onChange={(_, v: number) => setActiveTab(v)}
          aria-label="Encounter sections"
        >
          <StyledTab label="Details" />
          {referralsEnabled && <StyledTab label="Timeline" />}
        </StyledTabs>

        <Box pt={2}>
          {visibleTab === 0 && (
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              {encounter.elephantPatientId === null && encounterId && (
                <RoleGate can="editOwnExam">
                  <LinkPatientSection encounterId={encounterId} />
                </RoleGate>
              )}

              {billingCodesEnabled &&
                encounter.elephantPatientId === null &&
                encounterId && (
                  <RoleGate can="editOwnExam">
                    <PatientBillingSection
                      encounterId={encounterId}
                      savedPatient={encounterPatient}
                    />
                  </RoleGate>
                )}

              {awaitingImages && accessionNumber && (
                <AwaitingImagesSection accessionNumber={accessionNumber} />
              )}

              {pendingDicomImages.length > 0 && (
                <UnmatchedDicomSection
                  images={pendingDicomImages}
                  encounterId={encounterId}
                  examCollection={examCollection}
                  userId={user.id}
                />
              )}

              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 700,
                      fontSize: rem(17),
                      color: EPOCUS_TEXT_PRIMARY,
                    }}
                  >
                    Exams ({exams.length})
                  </Typography>
                  <RoleGate can="createEncounter">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() =>
                        navigate(linkToEpocusAddExam(encounterId ?? ""))
                      }
                      style={{
                        borderRadius: 8,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: rem(13),
                        minHeight: 36,
                        padding: "6px 14px",
                      }}
                    >
                      + Add Exam
                    </Button>
                  </RoleGate>
                </Box>

                <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                  {exams.length === 0 && (
                    <Typography
                      variant="body2"
                      style={{
                        color: EPOCUS_TEXT_SECONDARY,
                        fontStyle: "italic",
                        padding: "8px 0",
                      }}
                    >
                      No exams yet.
                    </Typography>
                  )}
                  {exams.map((exam) => {
                    const ExamIcon =
                      EXAM_ICONS[exam.examType] ?? UltrasoundScanner;
                    return (
                      <Box key={exam.id}>
                        <ExamCard
                          onClick={() => {
                            if (removingExamId === exam.id) return;
                            navigate(
                              linkToEpocusExam(encounterId ?? "", exam.id),
                            );
                          }}
                          onKeyDown={(e) => {
                            if (removingExamId === exam.id) return;
                            if (e.key === "Enter" || e.key === " ") {
                              navigate(
                                linkToEpocusExam(encounterId ?? "", exam.id),
                              );
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`${EXAM_LABELS[exam.examType]} exam, ${exam.status}`}
                        >
                          <ExamIconBox aria-hidden>
                            <ExamIcon
                              width={24}
                              height={24}
                              fill={EPOCUS_ICON_ACTIVE}
                            />
                          </ExamIconBox>
                          <Box flex={1} minWidth={0}>
                            <Typography
                              style={{
                                fontWeight: 700,
                                fontSize: rem(15),
                                color: EPOCUS_TEXT_PRIMARY,
                              }}
                            >
                              {EXAM_LABELS[exam.examType]}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{
                                color: EPOCUS_TEXT_SECONDARY,
                                fontSize: rem(13),
                              }}
                              noWrap
                            >
                              {exam.images.length} image
                              {exam.images.length !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                          <Chip
                            label={
                              exam.status === "submitted"
                                ? "Submitted"
                                : "Draft"
                            }
                            size="small"
                            style={{
                              backgroundColor:
                                exam.status === "submitted"
                                  ? "#dcfce7"
                                  : "#fef3c7",
                              color:
                                exam.status === "submitted"
                                  ? "#16a34a"
                                  : "#d97706",
                              fontWeight: 700,
                              fontSize: rem(12),
                              marginRight: 8,
                            }}
                          />
                          {exam.status === "draft" && (
                            <RoleGate can="editOwnExam">
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRemovingExamId(
                                    removingExamId === exam.id ? null : exam.id,
                                  );
                                }}
                                aria-label="Remove exam"
                                style={{
                                  minWidth: 0,
                                  padding: "4px 8px",
                                  color: EPOCUS_TEXT_SECONDARY,
                                  fontSize: rem(18),
                                }}
                              >
                                ✕
                              </Button>
                            </RoleGate>
                          )}
                          {exam.status !== "draft" && (
                            <Typography
                              style={{
                                color: EPOCUS_TEXT_SECONDARY,
                                fontSize: rem(18),
                              }}
                              aria-hidden
                            >
                              ›
                            </Typography>
                          )}
                        </ExamCard>
                        {removingExamId === exam.id && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            style={{
                              gap: 8,
                              padding: "8px 12px",
                              backgroundColor: "#fef2f2",
                              border: `1px solid ${EPOCUS_BORDER}`,
                              borderTop: "none",
                              borderRadius: "0 0 10px 10px",
                            }}
                          >
                            <Typography
                              style={{
                                fontSize: rem(13),
                                color: "#dc2626",
                                fontWeight: 600,
                                flex: 1,
                              }}
                            >
                              Remove this exam?
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => setRemovingExamId(null)}
                              style={{
                                fontSize: rem(13),
                                textTransform: "none",
                                color: EPOCUS_TEXT_SECONDARY,
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleRemoveExam(exam.id)}
                              style={{
                                fontSize: rem(13),
                                textTransform: "none",
                                backgroundColor: "#dc2626",
                                color: "#fff",
                                fontWeight: 700,
                              }}
                              disableElevation
                            >
                              Remove
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {submitError && (
                <Box
                  p={2}
                  style={{ backgroundColor: "#fef2f2", borderRadius: 10 }}
                  textAlign="center"
                >
                  <Typography
                    style={{
                      color: "#dc2626",
                      fontWeight: 600,
                      fontSize: rem(14),
                    }}
                  >
                    {submitError}
                  </Typography>
                </Box>
              )}

              {(status === "draft" || status === "imported") && (
                <RoleGate can="editOwnExam">
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!canSubmitForReview}
                    onClick={handleSubmitForReview}
                    style={{
                      borderRadius: 10,
                      padding: "14px 24px",
                      fontWeight: 700,
                      fontSize: rem(16),
                      textTransform: "none",
                      minHeight: 52,
                    }}
                    fullWidth
                  >
                    {canSubmitForReview
                      ? "Submit for Review"
                      : `Submit for Review (${exams.filter((e) => e.status === "submitted").length}/${exams.length} exams complete)`}
                  </Button>
                </RoleGate>
              )}

              {status === "pending_review" && (
                <Box
                  p={2}
                  style={{ backgroundColor: "#dbeafe", borderRadius: 10 }}
                  textAlign="center"
                >
                  <Typography
                    style={{
                      color: "#2563eb",
                      fontWeight: 700,
                      fontSize: rem(15),
                    }}
                  >
                    ✓ Submitted for review
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {referralsEnabled && visibleTab === 1 && encounterId && (
            <EncounterTimeline encounterId={encounterId} />
          )}
        </Box>
      </Box>

      {referralsEnabled && (
        <ReferPatientSheet
          open={referSheetOpen}
          onClose={() => setReferSheetOpen(false)}
          encounterId={encounterId ?? ""}
        />
      )}
    </PageRoot>
  );
};
